/* eslint-disable no-underscore-dangle */
/**
 * `/portal/dispatcher` — Pickup Hungary's office board: every operator's
 * drivers on one screen, for Yesterday · Today · Tomorrow · This week, or
 * any single day a dispatcher picks from the calendar (Budapest days, the
 * operator portal's own `rangeFor`).
 *
 * ONE LIST, IN TIME ORDER, ACROSS EVERY DRIVER. The board used to be twenty
 * little days, one per person; that answers "what is Anna doing today" and
 * nothing else, and it is not the question an office asks of a whole board.
 * A dispatcher wants to know what is happening at ten past four. So the
 * board now reads the way the office's own Daily Overview reads — down the
 * clock — and the two screens agree about the shape of a day.
 *
 * Nothing is lost by it. Every row names its driver and their operator where
 * the group heading used to, and Message / Call sit inside the row's fold;
 * "what is Anna doing today" is her name in the search box, which narrows
 * this same list to her trips, still in time order, and reads the whole
 * period through to answer where the grouping only ever saw a page.
 *
 * A DAY IS A BLOCK, headed by its name whenever the range holds more than
 * one: a flat time order across a week would put Monday 08:00 under Sunday
 * 19:00 with nothing to tell them apart. On a single day the page's own
 * subtitle already names it, so there is no heading and no wasted line.
 *
 * AND THE "NOW" LINE, which is what the ordering was for. One red rule down
 * the whole period, in the day it falls in: everything above it has started,
 * everything below is still ahead. Same component and same rule as the Daily
 * Overview and the driver's own day (`Components/NowLine`, `Utils/nowMarker`)
 * so the three cannot disagree about what has happened. It is drawn whenever
 * the range HOLDS today — which includes This week, where it is the most
 * useful thing on the screen — and never on a range that does not.
 *
 * WRITTEN FOR A PHONE, IN AN OFFICE, WITH SOMEBODY ON THE TELEPHONE. Three
 * things are within a thumb's reach before any scrolling: the period, a
 * search that finds one trip, and a filter for what needs doing something
 * about. All three NARROW the same list and never reorder it — time order is
 * how a dispatcher holds a day in their head, and a screen that resorted
 * itself under a tap would cost more than the ranking could win back.
 *
 * LIVE OR COVERAGE IS THE API'S CALL: the board reads `live` off the
 * response and never works it out from the tab, so the two can never
 * disagree. In practice only today is live — yesterday and a picked day are
 * coverage, exactly like tomorrow. Each read asks for the Budapest window
 * afresh, so a board left open past midnight moves on to the new day.
 *
 * A PERIOD ARRIVES A PAGE AT A TIME, as the reader scrolls (`has_more` and
 * `?page=`, the API's own paging). It used to stop at one big read and tell
 * whoever was reading it to choose a shorter period, which is the worst
 * answer to give someone on the road. The numbers above the list are the
 * WHOLE period's on every page — the API counts them there — so "no driver:
 * 0" can never be about page one; and a search or the attention filter reads
 * the rest of the period through rather than answering off the first page.
 *
 * NOTHING HERE BLANKS. A change of range keeps the board that is on screen
 * until the new one lands (and the heading describes what is SHOWN, from the
 * response's own `from`/`to`, never what has been asked for), a search and a
 * filter are done on rows already in hand, and a failed read keeps the last
 * good board and says so.
 *
 * TODAY IS A LIVE BOARD. Each driver's trips carry the step they last
 * tapped and when, so the office can see at a glance who is on the way, who
 * is waiting at a pickup, who is driving, who has finished and who has not
 * started. What needs doing something about — late, waiting, a no-show, a
 * running overtime clock — is lifted to the top and counted above the list.
 * It re-reads itself every BOARD_REFRESH_MS, and whenever the tab comes back
 * to the foreground; the clocks tick locally in between.
 *
 * TOMORROW AND LATER ARE COVERAGE, NOT ACTIVITY. The same list, but the
 * question is who is assigned and what still has nobody — so the screen says
 * so in as many words, and shows no step or delay at all (the API sends
 * none: a trip tomorrow cannot be late). A trip with nobody on it is red
 * wherever it falls in the day, which is what its own red-bordered section
 * used to say when the board had one.
 *
 * EVERY TRIP IS A CARD THAT STARTS CLOSED (CollapsibleSection). Closed, it
 * says when, which booking, where, and how it is going; opening it adds the
 * operator, the vehicle, the passengers and the customer. Assign and Details
 * sit beside the fold's handle, so a tap on the row folds it and a tap on a
 * control does that control.
 *
 * IT ACTS ON ONE THING: who is on a trip. Every row offers Assign / Change,
 * which opens the portal's shared AssignSheet with the office's source
 * (`dispatcherAssignSource`) — operator, driver, vehicle and the operator's
 * fee. What comes back replaces that one row on the spot, so the board shows
 * the change without re-reading; a quiet refresh follows to bring the counts
 * above the list back in step. Nothing else on this screen writes anything.
 *
 * MONEY: none on this screen. `/portal/dispatcher/board` sends no price,
 * fee, cash or profit, and nothing here reads such a field — the screen
 * renders named fields only, never the trip object whole. The operator's fee
 * lives in the assign sheet and only there. The one amount on the board
 * itself is a driver's own expense claim, in the claims panel, as it was filed.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import useUrlViewState from '../../../Hooks/useUrlViewState';
import useAskSubject from './askSubject';
import {
  fetchDispatcherBoard,
  fetchDispatcherExpenses,
} from '../../../Services/DispatcherPortalApi';
import { formatTime } from '../../../Utils/timezone';
import { telUrl } from '../../../Utils/contactLinks';
import { CalendarIcon, ChevronRightIcon } from '../Components/Icons';
import DayPickerSheet from '../Components/DayPickerSheet';
import Toast from '../Components/Toast';
import useNow from '../useNow';
import AssignSheet from '../Operator/AssignSheet';
import { dispatcherAssignSource } from '../assignSources';
import NowLine, { NOW_LINE_PORTAL } from '../../../Components/NowLine';
import { nowSplitIndex, nowTimeLabel, periodHoldsNow } from '../../../Utils/nowMarker';
import AssignButton from './AssignButton';
import CollapsibleSection from './CollapsibleSection';
import DriverContact from './DriverContact';
import Pill from '../Operator/Pill';
import RangeTabs from '../Operator/RangeTabs';
import {
  DAY_PICKER_COPY_EN,
  driverText,
  formatMoney,
  NOW_LABEL_EN,
  operatorLoadErrorMessage,
  phoneReleaseText,
  plural,
  rangeFor,
  rangeHeading,
  roleLabel,
  routeLabel,
  tripMeta,
  vehicleText,
} from '../Operator/operatorFormat';
import {
  appendPage,
  attentionCount,
  attentionOf,
  BOARD_REFRESH_MS,
  boardByTime,
  CALENDAR_KEY,
  CLOSED_BY_OFFICE,
  countsLabel,
  currentStep,
  DISPATCHER_RANGES,
  expenseStatus,
  expenseSubmitter,
  expenseTypeLabel,
  filterTrips,
  hasMorePages,
  hasRunningClock,
  isBoardCapped,
  lateBy,
  operatorName,
  overtimeRunningFor,
  periodFor,
  pickedDayRange,
  rangeMeaning,
  replaceSlot,
  resultsLabel,
  slotKey,
  stepText,
  whenLabel,
} from './dispatcherFormat';

/**
 * What the board is NARROWED to — the period, the day the calendar was
 * given, the search and the attention filter — in the URL rather than in
 * useState.
 *
 * React Router unmounts this screen the moment a row's Details is opened,
 * so a `useState` view is gone by the time the office comes back: the
 * period silently reset to Today and the search box emptied itself, which
 * on a phone, with somebody on the telephone, means reading the booking
 * reference out again. The URL survives that, because going Back IS the
 * browser restoring a URL; ScrollMemory then returns the dispatcher to the
 * row they were reading, and `Hooks/useBackTo` on the trip screen is what
 * makes its back control a POP rather than a fresh push. See
 * `Hooks/useUrlViewState`.
 *
 * Every value here is the screen's own default, so an untouched board keeps
 * a clean address and a link a dispatcher sends carries only what they
 * actually chose. `day` is empty rather than today's date, because today is
 * whatever `rangeFor('today')` says at the moment it is asked — a constant
 * written at import time stops being true at midnight.
 *
 * The search goes in per keystroke, undebounced, because nothing on this
 * screen FETCHES on it: a search narrows rows already in hand (`filterTrips`),
 * so the only cost of a keystroke is a REPLACE of the current history entry,
 * and a REPLACE is deliberately not a place the reader has travelled to.
 */
const BOARD_VIEW = {
  range: 'today', day: '', q: '', attention: false,
};

/** Every period this screen offers; `?range=` is hand-editable. */
const RANGE_KEYS = [...DISPATCHER_RANGES.map((option) => option.key), CALENDAR_KEY];

/**
 * The period the URL is asking for, or this screen's default when it asks
 * for something the board does not offer — an unrecognised `?range=` shows
 * the default board, never an empty tab strip over a heading nobody chose.
 *
 * `calendar` is a period only when a readable day came with it. On its own —
 * a URL hand-edited, truncated or pasted out of a message — `periodFor`
 * answers null, `load` returns without asking for anything, and the board
 * would sit on "Loading the board…" for ever.
 */
const rangeKeyFrom = (range, day) => {
  if (!RANGE_KEYS.includes(range)) return BOARD_VIEW.range;
  if (range === CALENDAR_KEY && !pickedDayRange(day)) return BOARD_VIEW.range;
  return range;
};

const EMPTY = {
  trips: [],
  summary: {
    trips: 0,
    drivers_working: 0,
    unassigned: 0,
    needs_attention: 0,
    attention: { late: 0, waiting: 0, no_show: 0 },
  },
};

/** The numbers above the list: how much work, how many people, what is missing. */
function Summary({ summary, live }) {
  const attention = summary.attention || {};
  const tiles = [
    { key: 'Trips', value: summary.trips },
    { key: 'Drivers working', value: summary.drivers_working },
    { key: 'No driver', value: summary.unassigned, alert: summary.unassigned > 0 },
    // Only a live board has anything to be late for.
    ...(live ? [
      { key: 'Late', value: attention.late || 0, alert: (attention.late || 0) > 0 },
      { key: 'Waiting', value: attention.waiting || 0, alert: (attention.waiting || 0) > 0 },
      { key: 'No-shows', value: attention.no_show || 0, alert: (attention.no_show || 0) > 0 },
    ] : []),
  ];

  return (
    <dl className="pp-dispatch__summary">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`pp-dispatch__tile${tile.alert ? ' is-alert' : ''}`}
        >
          <dt>{tile.key}</dt>
          <dd>{tile.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * How to REACH the customer, with the phone only inside the API's 24-hour
 * window. Their name is not repeated here: the card carries it while closed,
 * which is where a dispatcher looks for it, and printing it twice on an open
 * card just makes the card longer.
 */
function Customer({ trip }) {
  const pending = phoneReleaseText(trip);

  return (
    <div className="pp-dispatch__customer">
      {trip.customer_phone ? (
        <a className="pp-op-phone" href={telUrl(trip.customer_phone)}>{trip.customer_phone}</a>
      ) : null}
      {!trip.customer_phone && pending ? <span className="pp-op-small">{pending}</span> : null}
    </div>
  );
}

/**
 * One trip, as a CARD THAT STARTS CLOSED.
 *
 * Closed it still says everything a dispatcher scans for: when, which
 * booking, where it goes, and — on a live board — the step, what needs
 * attention, and whether anybody is on it. Opening it adds the detail that
 * only matters once a trip is the one being dealt with: whose trip it is,
 * the role and vehicle, the passengers, and the customer with their phone.
 *
 * TWO CONTROLS, NO FIGHT. The summary row IS the fold's handle (a real
 * button, `aria-expanded`, named by the very time, reference and route it
 * shows), and Assign and Details sit BESIDE it in their own area — never
 * nested inside it. Details stays a link, because it navigates, but it is a
 * proper 44px target now rather than a line of text, and it keeps its
 * accessible name ("Details PU_DASH_…").
 *
 * WHOSE TRIP IT IS, on every row. The board once said this once, as the
 * heading of a driver's group; a list in time order has no such heading, so
 * the driver and their operator moved onto the row itself — on their own
 * line under the rest of the summary, where a name is never squeezed between
 * a route and a reference. A row with nobody on it says the operator alone,
 * because the office still needs to know whom to chase, and the "No driver"
 * pill beside it already says the rest.
 *
 * REACHING THE DRIVER is inside the fold, with the vehicle and the
 * customer's phone. Message and Call on every closed row would be two more
 * 44px targets on each of twenty rows; opening the one trip being dealt with
 * is the tap that earns them, and it is the same pair the trip screen's Crew
 * card offers.
 */
function TripRow({
  trip, live, multiDay, now, onAssign,
}) {
  const step = currentStep(trip);
  const attention = live ? attentionOf(trip) : null;
  const late = live ? lateBy(trip, now) : '';
  const overtime = live ? overtimeRunningFor(trip, now) : '';
  const role = trip.role && trip.role !== 'driver' ? roleLabel(trip.role) : '';
  // The car this slot is on — a driver can be in a different one tomorrow,
  // so it belongs on the trip, not on the driver's heading.
  const vehicle = trip.vehicle ? vehicleText(trip) : '';

  const title = (
    <span className="pp-dispatch__trip-head">
      <span className="pp-op-time">{whenLabel(trip, multiDay)}</span>
      <span className="pp-op-small">{trip.booking_reference}</span>
      {/* The passenger's name, on the CLOSED card: it is who a dispatcher is
          asked about by name on the telephone, so needing to open the card to
          find it made the closed card useless for the commonest question.
          Their phone stays inside, under the 24-hour rule. */}
      {trip.customer_name ? <span className="pp-op-small">{trip.customer_name}</span> : null}
      <span className="pp-op-route">{routeLabel(trip)}</span>
      <span className="pp-dispatch__who">
        {driverText(trip) ? <span className="pp-op-small">{driverText(trip)}</span> : null}
        <span className="pp-op-small">{trip.operator ? operatorName(trip) : 'No operator'}</span>
      </span>
    </span>
  );

  const state = (
    <span className="pp-dispatch__trip-state">
      {live ? (
        <>
          {/* Nobody on it: there is no step to report, only the gap. */}
          {trip.needs_driver ? <Pill tone="bad">No driver</Pill> : (
            <Pill tone={step.tone}>{stepText(step)}</Pill>
          )}
          {step.closedByOffice ? <Pill tone="muted">{CLOSED_BY_OFFICE}</Pill> : null}
          {attention ? <Pill tone={attention.tone}>{attention.label}</Pill> : null}
        </>
      ) : (
        <Pill tone={trip.needs_driver ? 'bad' : 'muted'}>
          {trip.needs_driver ? 'No driver' : 'Assigned'}
        </Pill>
      )}
    </span>
  );

  const actions = (
    <>
      <AssignButton trip={trip} onAssign={onAssign} />
      <Link
        className="pp-btn pp-btn--sm pp-btn--outline"
        to={`/portal/dispatcher/trip/${trip._id}`}
        state={{ trip }}
        aria-label={`Details ${trip.booking_reference}`}
      >
        Details
        <ChevronRightIcon size={14} width={2.5} />
      </Link>
    </>
  );

  // Only a real alarm reddens the row. "Driver not using app" is a note in
  // the same place, and painting most of the board with it would leave the
  // office with nothing to look at.
  //
  // A trip with NOBODY ON IT is one of them, on a live board and a coverage
  // one alike. It used to be red by virtue of sitting in its own red-bordered
  // "No driver assigned" section; in time order there is no such section, so
  // the row carries the colour that section carried. It is the same rule the
  // "Needs attention" chip counts by (`attentionRow`), so the tint and the
  // number cannot disagree.
  const alarm = Boolean(trip.needs_driver) || Boolean(attention && attention.tone !== 'muted');

  return (
    <li className={`pp-dispatch__trip${alarm ? ' is-alert' : ''}`}>
      <CollapsibleSection title={title} summary={state} actions={actions}>
        <span className="pp-op-small">
          {[role, tripMeta(trip), vehicle].filter(Boolean).join(' · ')}
        </span>
        {/* Where the driver is NAMED is where the office reaches them. */}
        <DriverContact name={driverText(trip)} phone={trip.driver?.phone_number} />
        <Customer trip={trip} />
        {/* Plain text, not a live region: a clock on every row, re-read
            every 30 seconds, would talk over the whole board. */}
        {late ? <span className="pp-dispatch__clock">{late}</span> : null}
        {overtime ? (
          <span className="pp-dispatch__clock">{`Overtime running · ${overtime}`}</span>
        ) : null}
      </CollapsibleSection>
    </li>
  );
}

/**
 * ONE DAY OF THE BOARD: its trips in pickup order, with the "now" line where
 * it divides them.
 *
 * `splitAt` is the row to draw the marker ABOVE — `trips.length` draws it
 * under the last one, and `-1` draws nothing. The index is worked out once
 * for the whole period (`nowSplitIndex` over the flat list) and handed down,
 * so a week's worth of days cannot end up with a red rule apiece: exactly one
 * day is ever given a number that is in range.
 *
 * There is no `onlyBetween` switch here any more. That existed because the
 * board was MANY lists, one per driver, where a line pinned to the top or the
 * bottom of a group divided nothing. One list in time order is exactly the
 * case the Daily Overview and the driver's own day are: "everything today is
 * still ahead" and "today is done" are both worth saying, and the line says
 * them by sitting at the top or the bottom.
 */
function TripList({
  trips, splitAt = -1, marker, live, multiDay, now, onAssign,
}) {
  return (
    <ul className="pp-dispatch__trips">
      {trips.map((trip, index) => (
        <React.Fragment key={slotKey(trip)}>
          {splitAt === index ? <li className="pp-dispatch__now">{marker}</li> : null}
          <TripRow
            trip={trip}
            live={live}
            multiDay={multiDay}
            now={now}
            onAssign={onAssign}
          />
        </React.Fragment>
      ))}
      {splitAt === trips.length ? <li className="pp-dispatch__now">{marker}</li> : null}
    </ul>
  );
}

/**
 * One Budapest day of a board that may span several.
 *
 * The heading is drawn only on a MULTI-DAY range: on a single day the page's
 * own subtitle already names it, and a second copy of "Wednesday 16
 * September" above the only list on the screen is a line of a phone's height
 * spent saying nothing. On a week it is the whole point — a flat time order
 * across seven days with no day said out loud is unreadable.
 */
function DayGroup({
  day, showsDay, splitAt, marker, live, multiDay, now, onAssign,
}) {
  return (
    <section className="pp-dispatch__group" aria-label={day.label}>
      {showsDay ? (
        <header className="pp-dispatch__group-head">
          <h2 className="pp-dispatch__day">{day.label}</h2>
          <div className="pp-op-small">{plural(day.trips.length, 'trip', 'trips')}</div>
        </header>
      ) : null}
      <TripList
        trips={day.trips}
        splitAt={splitAt}
        marker={marker}
        live={live}
        multiDay={multiDay}
        now={now}
        onAssign={onAssign}
      />
    </section>
  );
}

/** Today's claims, PH-wide — the one place an amount appears on this board. */
function Expenses({ expenses, error }) {
  if (error) {
    return (
      <section className="pp-op-card pp-dispatch__expenses" aria-label="Expense claims">
        <h2 className="pp-dispatch__section-title">Expense claims filed today</h2>
        <div className="pp-op-error" role="alert">{error}</div>
      </section>
    );
  }

  return (
    <section className="pp-op-card pp-dispatch__expenses" aria-label="Expense claims">
      <h2 className="pp-dispatch__section-title">
        {`Expense claims filed today · ${expenses.length}`}
      </h2>
      {!expenses.length ? (
        <div className="pp-op-empty">Nothing filed today.</div>
      ) : (
        <table className="pp-op-table">
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Type</th>
              <th scope="col">Filed by</th>
              <th scope="col">Trip</th>
              <th scope="col" className="is-num">Amount</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => {
              const status = expenseStatus(expense.status);

              return (
                <tr key={expense._id}>
                  <td data-label="Time">{formatTime(expense.created_at)}</td>
                  <td data-label="Type">{expenseTypeLabel(expense.type)}</td>
                  <td data-label="Filed by">
                    <div>{expenseSubmitter(expense)}</div>
                    <div className="pp-op-small">{expense.operator?.name || ''}</div>
                  </td>
                  <td data-label="Trip">{expense.booking_reference || '—'}</td>
                  <td data-label="Amount" className="is-num">
                    {formatMoney(expense.amount, expense.currency)}
                  </td>
                  <td data-label="Status">
                    <Pill tone={status.tone}>{status.label}</Pill>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * The period: four windows, and a calendar icon for any other day.
 *
 * ONE DAY, NOT A SPAN. The week tab is what a range is for; what a
 * dispatcher means by reaching for a calendar is a particular day — the 4th,
 * the Saturday of the race weekend — so that is all the icon offers. The
 * board used to show two native date inputs inside a fifth tab, which made
 * every single day a span of one and put a 31-day rule on a screen nobody
 * had asked a range of.
 *
 * It is the DRIVER'S OWN PICKER (DayPickerSheet, opened from the week strip
 * by this very icon): the app's react-datetime month view inline in the
 * portal's bottom sheet, with "today" judged in Budapest rather than on the
 * device. English here, Hungarian there, one component. A dispatcher has no
 * per-day trip counts to hand, so the days carry no dots.
 *
 * The icon is a real 44px button with a name of its own, and the sheet is
 * the portal's, so a phone gets a full-width month it can thumb through
 * rather than a popup of ours to get wrong on a small screen.
 */
function RangePicker({
  value, onChange, open, onOpen, busy,
}) {
  return (
    <div className="pp-dispatch__ranges">
      <RangeTabs options={DISPATCHER_RANGES} value={value} onChange={onChange} busy={busy} />
      <button
        type="button"
        className={`pp-dispatch__calendar${value === CALENDAR_KEY ? ' is-active' : ''}`}
        aria-label="Pick a day"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={onOpen}
      >
        <CalendarIcon size={20} />
      </button>
    </div>
  );
}

/**
 * Finding ONE trip, and seeing what needs doing something about.
 *
 * Both are here, above the list and sticky, because both are used with a
 * customer or a driver already on the telephone: no scrolling to reach them,
 * a 44px target each, and the search clears in one tap.
 *
 * SEARCHING IS DONE ON THE ROWS IN HAND, not by asking the API. A day or a
 * week is a few hundred rows, so a typed character answers in the same frame
 * with no round trip, on a phone that may be on one bar of signal; and the
 * five things a dispatcher says out loud — the reference, the customer, the
 * driver, the operator, the plate — live on the row, whereas the booking
 * holds only ids for three of them and the API could not match them in one
 * query. What makes it honest on a long period is the pager: while a search
 * or the filter is on, the board reads the rest of the period through, and
 * says "so far" until it has.
 */
function FindTrip({
  query, onQuery, onlyAttention, onAttention, attention, results,
}) {
  return (
    <div className="pp-dispatch__find">
      <div className="pp-dispatch__search">
        <input
          type="search"
          className="pp-dispatch__search-input"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="Booking, name, driver, operator, plate"
          aria-label="Find a trip"
        />
        {query ? (
          <button
            type="button"
            className="pp-dispatch__search-clear"
            onClick={() => onQuery('')}
            aria-label="Clear the search"
          >
            ×
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className={`pp-btn pp-btn--sm pp-dispatch__attention${onlyAttention ? ' is-on' : ''}`}
        aria-pressed={onlyAttention}
        onClick={() => onAttention(!onlyAttention)}
      >
        {`Needs attention · ${attention}`}
      </button>
      {results ? <p className="pp-dispatch__results" role="status">{results}</p> : null}
    </div>
  );
}

export default function DispatcherBoard() {
  const { view, setView } = useUrlViewState(BOARD_VIEW);
  const rangeKey = rangeKeyFrom(view.range, view.day);
  const setRangeKey = useCallback((next) => setView({ range: next }), [setView]);
  // The day the calendar holds, seeded with whatever was on screen when the
  // icon was tapped, so the month opens on the day the dispatcher was
  // already looking at. Whether its SHEET is open is this visit's own
  // business and stays in state: an open sheet is not a board worth sharing.
  const pickedDay = pickedDayRange(view.day) ? view.day : rangeFor('today').from;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // How many pages of this period are held, and whether the next one is on
  // the wire. The count is a ref, not state: it is only ever read by the
  // refresh loop and the scroll, and neither should re-arm its timer when a
  // page lands — the rows themselves are what re-render.
  const pagesHeld = useRef(0);
  const [reading, setReading] = useState(false);
  const readingMore = useRef(false);
  const [error, setError] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [expensesError, setExpensesError] = useState('');
  // What the dispatcher has narrowed the same list to, from the same URL.
  const { q: query, attention: onlyAttention } = view;
  const setQuery = useCallback((next) => setView({ q: next }), [setView]);
  const setOnlyAttention = useCallback((next) => setView({ attention: next }), [setView]);
  // The row whose assign sheet is open, and the line under it once saved.
  const [assigning, setAssigning] = useState(null);
  const [notice, setNotice] = useState('');

  // The picked day, but only while the board is actually on it: seeding the
  // month view with the day already on screen must not re-read anything.
  const pickedFrom = rangeKey === CALENDAR_KEY ? pickedDay : '';

  // Live or coverage is the API's call (`live`, true only for today), not a
  // second rule here. Until this range's first answer lands there is
  // nothing to be live about.
  const live = data.live === true;

  // Only the latest request may land. Switching Today → This week while
  // Today is still on the wire must not show Today's trips under the week.
  const latestLoad = useRef(0);

  /**
   * The window to ask for, worked out afresh at every read — so a board left
   * open past midnight moves on to the new day — or nothing at all if the
   * calendar somehow holds no day.
   */
  const windowFor = useCallback(
    () => periodFor(rangeKey, pickedFrom),
    [rangeKey, pickedFrom],
  );

  /**
   * Read the first `count` pages of this period and put them on screen in
   * place of what is there.
   *
   * Every page carries the same whole-period counts and the same `from`/`to`,
   * so the last answer is the board's, with the rows of all of them behind
   * it. A refresh re-reads exactly what is held — which on a live board is
   * one page, because a day is one page.
   */
  const load = useCallback(async ({ quiet = false, count = 1 } = {}) => {
    const period = windowFor();

    if (!period) return;

    latestLoad.current += 1;
    const ticket = latestLoad.current;
    // Said BEFORE the answers land, so nothing in between — a poll, a tap on
    // Load more — can ask for a page of a period nobody is reading.
    pagesHeld.current = count;
    // A refresh must not blank the board the office is reading, and neither
    // must a change of range: the spinner is only for a board with nothing
    // on it yet.
    if (!quiet) setLoading(true);
    try {
      const answers = await Promise.all(
        Array.from({ length: count }, (ignored, index) => fetchDispatcherBoard({
          ...period, page: index + 1,
        })),
      );
      if (ticket === latestLoad.current) {
        setData({
          ...answers[answers.length - 1],
          trips: answers.reduce((held, answer) => appendPage(held, answer.trips), []),
        });
        setError('');
      }
    } catch (failure) {
      if (ticket !== latestLoad.current) return;
      // Whatever failed — the first read, a refresh or a change of range —
      // the last good board stays on screen and the error is said above it.
      setError(operatorLoadErrorMessage(failure));
    } finally {
      // Whichever read is the newest ends the spinner, quiet or not: a
      // refresh that overtakes the first read must not leave it spinning.
      if (ticket === latestLoad.current) setLoading(false);
    }
  }, [windowFor]);

  /**
   * The next page, appended — what the scroll and a narrowed board ask for.
   *
   * It does NOT take a new ticket: it adds to what is held rather than
   * replacing it. A change of range takes one, and this page is then
   * dropped, because it belongs to a period nobody is looking at any more.
   */
  const readMore = useCallback(async () => {
    const period = windowFor();

    if (!period || readingMore.current) return;

    const ticket = latestLoad.current;
    const next = pagesHeld.current + 1;
    readingMore.current = true;
    setReading(true);
    try {
      const answer = await fetchDispatcherBoard({ ...period, page: next });
      if (ticket === latestLoad.current) {
        setData((held) => ({ ...answer, trips: appendPage(held.trips, answer.trips) }));
        pagesHeld.current = next;
        setError('');
      }
    } catch (failure) {
      if (ticket === latestLoad.current) setError(operatorLoadErrorMessage(failure));
    } finally {
      readingMore.current = false;
      if (ticket === latestLoad.current) setReading(false);
    }
  }, [windowFor]);

  useEffect(() => {
    load();
    // A new period, or leaving the screen: whatever is on the wire is stale.
    return () => { latestLoad.current += 1; };
  }, [load]);

  // The refresh loop. Only a LIVE board polls — nothing about next Tuesday
  // changes minute to minute — and a board whose last read failed, so a
  // blip does not leave it dead until somebody changes tab. Coming back to
  // the tab re-reads whatever range is on screen, which is the cheapest way
  // to be current after a while away and is also what makes a coverage
  // range refresh at all.
  const polling = live || Boolean(error);

  useEffect(() => {
    // Re-read exactly the pages that are held, so a dispatcher who has
    // scrolled into a week does not have it collapse back to its first page
    // under them. A live board is one day, which is one page.
    const refresh = () => load({ quiet: true, count: Math.max(pagesHeld.current, 1) });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', onVisible);

    if (!polling) {
      return () => document.removeEventListener('visibilitychange', onVisible);
    }

    const timer = setInterval(refresh, BOARD_REFRESH_MS);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [polling, load]);

  // Today's claims, whatever range the trips are showing: they are about
  // what came in today, not about the window. Re-read on the board's beat,
  // and — like the board — for the Budapest day of each read.
  useEffect(() => {
    let cancelled = false;

    const read = () => fetchDispatcherExpenses(rangeFor('today'))
      .then((rows) => {
        if (!cancelled) {
          setExpenses(rows);
          setExpensesError('');
        }
      })
      .catch((failure) => {
        if (!cancelled) setExpensesError(operatorLoadErrorMessage(failure));
      });

    read();
    const timer = setInterval(read, BOARD_REFRESH_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  /**
   * What the assign endpoint answered with, in place of the row the sheet
   * was opened on — so the board shows the change at once, with no reload
   * and no spinner. The counts above the list are the API's own, so a quiet
   * re-read follows to bring them back in step.
   */
  const assigned = useCallback((updated) => {
    setData((shown) => ({ ...shown, trips: replaceSlot(shown.trips, assigning, updated) }));
    setNotice(`${assigning?.booking_reference}: saved, the customer has been notified`);
    setAssigning(null);
    load({ quiet: true, count: Math.max(pagesHeld.current, 1) });
  }, [assigning, load]);

  const { trips, summary } = data;
  const more = hasMorePages(data);
  // Narrowing is only honest once the whole period has been read: a search
  // answering off page one would tell a dispatcher a booking does not exist.
  const narrowed = Boolean(query) || onlyAttention;

  useEffect(() => {
    if (!narrowed || !more || reading || loading || error) return;
    readMore();
  }, [narrowed, more, reading, loading, error, readMore]);

  /**
   * The rest of the period, as the reader reaches the end of what is here.
   *
   * `rootMargin` starts the next page well before the last row is on screen,
   * so scrolling does not stop and wait. Where there is no observer at all
   * (an old browser, a test), the button inside the sentinel is the same
   * action by hand — the board is never left with rows it cannot reach.
   */
  const endOfList = useRef(null);

  useEffect(() => {
    const node = endOfList.current;

    if (!node || !more || typeof IntersectionObserver !== 'function') return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) readMore();
    }, { rootMargin: '600px' });

    observer.observe(node);

    return () => observer.disconnect();
  }, [more, readMore]);

  const shown = useMemo(
    () => filterTrips(trips, { query, onlyAttention }),
    [trips, query, onlyAttention],
  );
  const { ordered, days } = useMemo(() => boardByTime(shown), [shown]);

  // What is SHOWN names itself: the range the answer came back for, not the
  // one that has just been asked for. A board mid-change says the day it is
  // still showing rather than labelling it with a day it has not read yet.
  const range = data.from && data.to
    ? { from: data.from, to: data.to }
    : (windowFor() || rangeFor('today'));

  /**
   * Does the period on screen CONTAIN this moment — i.e. is there a "now"
   * to draw at all?
   *
   * Not `live`. `live` is the API's word for "today, so a step or a delay
   * means something", and Yesterday / Tomorrow / a picked day are rightly
   * not it. But THIS WEEK holds today somewhere in its middle, and on a
   * week board the line is the most useful thing on the screen: it is the
   * boundary between the days that have happened and the days that have
   * not. So the test is the honest one — today, in Budapest, inside the
   * range the board is showing — and it answers no for every range that
   * does not hold it.
   *
   * `Date.now()` rather than the ticking clock below, which cannot be read
   * here: the clock only runs BECAUSE this said yes, so asking it first
   * would be circular. It decides only WHICH DAY we are in, which no clock
   * tick changes but one, and the board re-reads itself every minute anyway.
   */
  const showsNow = periodHoldsNow(range.from, range.to, Date.now());

  // Rows worth keeping, and a read in flight that will replace them. A
  // first paint has no rows to quieten, and a quiet refresh (the poll, a
  // save) never sets `loading` at all, so this is exactly "a period was
  // tapped and the board is still showing the last one".
  const quietRows = loading && days.length > 0;

  // A local clock, only while something on screen is actually counting —
  // the waiting and overtime timers, or the now line walking down the day.
  const ticking = (live && hasRunningClock(shown)) || showsNow;
  const now = useNow(ticking, 30 * 1000);
  const multiDay = range.from !== range.to;
  const heading = rangeHeading(range);
  /**
   * Tell the frame's assistant launcher which period is on screen, so a
   * question opens with it already named. `heading` and not the query string:
   * this is the range an answer actually came back for (see `range` above), so
   * a board mid-change never tells the agent about a day it has not read.
   */
  useAskSubject('board', heading);
  const attention = attentionCount(summary, trips);
  /**
   * WHERE THE CLOCK HAS GOT TO, once down the whole period.
   *
   * Counted over the FLAT list — the number of trips at or before now —
   * rather than per day, so the line is placed once and drawn once however
   * many days are on screen. `splitFor` then asks each day "is that index
   * one of mine": exactly one day can answer yes, and only the last day may
   * claim the index that sits past its final row (the marker that closes a
   * finished period).
   */
  const nowAt = showsNow ? nowSplitIndex(ordered, now) : -1;
  const splitFor = (day, isLast) => {
    const at = nowAt - day.offset;

    if (nowAt < 0 || at < 0) return -1;
    if (at < day.trips.length) return at;

    return isLast && at === day.trips.length ? at : -1;
  };
  const marker = (
    <NowLine block={NOW_LINE_PORTAL} label={NOW_LABEL_EN} time={nowTimeLabel(now)} />
  );
  const results = resultsLabel({
    matches: shown.length, query, onlyAttention, complete: !more,
  });

  /** The month opens on the day already on screen, not on some other month. */
  const openCalendar = useCallback(() => {
    setView({ day: range.from });
    setCalendarOpen(true);
  }, [range.from, setView]);

  /** A day off the month view IS the board's period, exactly as a tab is. */
  const pickDay = useCallback((day) => {
    // ONE write, so one REPLACE: the day and the period move together, and
    // the board is never momentarily on `calendar` with no day to show.
    setView({ day, range: CALENDAR_KEY });
    setCalendarOpen(false);
  }, [setView]);

  return (
    <div className="pp-op-page pp-dispatch__page">
      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">Dispatch board</h1>
          <div className="pp-op-subtitle">{`${heading} · ${countsLabel(summary)}`}</div>
          {/* Said once this range's own answer is in: the API decides. */}
          {data.generated_at ? (
            <div className="pp-dispatch__meaning" role="status">{rangeMeaning(live)}</div>
          ) : null}
        </div>
        <div className="pp-op-page__tools">
          <RangePicker
            value={rangeKey}
            onChange={setRangeKey}
            open={calendarOpen}
            onOpen={openCalendar}
            busy={loading}
          />
          {/* One line, whichever it says, so nothing moves as a read lands. */}
          {data.generated_at || loading ? (
            <div className="pp-op-small pp-dispatch__updated">
              {loading ? 'Loading…' : `Updated ${formatTime(data.generated_at)}`}
            </div>
          ) : null}
        </div>
      </div>

      <Summary summary={summary} live={live} />

      <FindTrip
        query={query}
        onQuery={setQuery}
        onlyAttention={onlyAttention}
        onAttention={setOnlyAttention}
        attention={attention}
        results={results}
      />

      {error ? <div className="pp-op-error" role="alert">{error}</div> : null}

      {loading && !data.generated_at ? (
        <div className="pp-state" role="status">Loading the board…</div>
      ) : null}

      {data.generated_at && !ordered.length && !error ? (
        <div className="pp-op-card">
          <div className="pp-op-empty">
            {narrowed
              ? 'Nothing here matches. Clear the search to see the whole period.'
              : 'No trips in this period.'}
          </div>
        </div>
      ) : null}

      {/* The period that is SHOWN. A new one being read does not tear this
          down — the rows stay where they are and go quiet (`pp-op-busy`,
          Operator.scss) until the answer lands. The feedback that the tap
          registered is the bar under the tabs; this is only the board
          saying the rows under it are a moment out of date. */}
      <div className={quietRows ? 'pp-op-busy' : undefined} aria-busy={quietRows || undefined}>
        {days.map((day, index) => (
          <DayGroup
            key={day.key || day.label}
            day={day}
            showsDay={multiDay}
            splitAt={splitFor(day, index === days.length - 1)}
            marker={marker}
            live={live}
            multiDay={multiDay}
            now={now}
            onAssign={setAssigning}
          />
        ))}
      </div>

      {/* The end of what has been read. Reaching it asks for the next page;
          the button is the same action for anything without an observer. */}
      {more ? (
        <div className="pp-dispatch__more" ref={endOfList}>
          {reading ? (
            <span className="pp-op-small" role="status">Reading more of this period…</span>
          ) : (
            <button type="button" className="pp-btn pp-btn--outline" onClick={readMore}>
              Load more
            </button>
          )}
        </div>
      ) : null}

      {/* The one limit paging cannot reach past: a period holding more than
          the board will serve at all. `total` counts bookings and `trips`
          counts rows, so neither number is quoted against the other. */}
      {isBoardCapped(data) ? (
        <p className="pp-op-footnote">
          This period holds more trips than the board can show. Pick fewer days to see the rest.
        </p>
      ) : null}

      <Expenses expenses={expenses} error={expensesError} />

      {/* The driver's own month view, in English: one day, Budapest's today
          marked, and no dots — the office has no per-day counts to hand. */}
      {calendarOpen ? (
        <DayPickerSheet
          selected={pickedDay}
          today={rangeFor('today').from}
          copy={DAY_PICKER_COPY_EN}
          onPick={pickDay}
          onClose={() => setCalendarOpen(false)}
        />
      ) : null}

      {assigning ? (
        <AssignSheet
          trip={assigning}
          source={dispatcherAssignSource}
          onClose={() => setAssigning(null)}
          onAssigned={assigned}
        />
      ) : null}

      <Toast message={notice} onDone={() => setNotice('')} />
    </div>
  );
}
